import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../store'
import { getFoodAreaRestaurants } from '../store/foodAreaSlice'

const DEFAULT_POLL_AFTER_MS = 1200
// Deliberately short: a real backend backfill takes minutes, so it will never
// resolve inside the poll window anyway. A brief 2-attempt (~3s) check reads as a
// normal loading blip; anything longer just shows a misleading "finding nearby…"
// spinner on areas that are (currently) permanently empty. Once areas are populated
// at generation time, the initial fetch returns restaurants and this rarely fires.
const DEFAULT_MAX_ATTEMPTS = 2
const BACKOFF_MS = 800

interface FoodAreaPollParams {
  foodAreaId?: string
  latitude?: number
  longitude?: number
}

/**
 * Conditional food-area backfill poll. When a freshly-seeded city's food area
 * comes back with zero restaurants (its restaurants are still being ingested on
 * the backend), poll the restaurants endpoint a few times with backoff before
 * falling back to the empty state. Mirrors the landing-backfill pattern in
 * `useLandingPoll` (poll-after + bounded attempts + backoff).
 *
 * Only starts once per food area, and only after the initial fetch has settled
 * empty — so the normal, already-populated case is never affected. Returns
 * whether a backfill cycle is currently active, so the UI can render a
 * "finding restaurants nearby…" affordance instead of the empty state.
 */
export function useFoodAreaRestaurantsPoll(
  params: FoodAreaPollParams,
  restaurantCount: number,
  isLoading: boolean,
): boolean {
  const dispatch = useDispatch<AppDispatch>()
  const [backfilling, setBackfilling] = useState(false)
  const cancelledRef = useRef(false)
  const polledAreaRef = useRef<string | null>(null)
  const pollInProgressRef = useRef(false)
  const prevLoadingRef = useRef(false)

  const { foodAreaId, latitude, longitude } = params

  // Reset per-area state when the user opens a different food area.
  useEffect(() => {
    if (!foodAreaId) return
    if (polledAreaRef.current !== null && polledAreaRef.current !== foodAreaId) {
      polledAreaRef.current = null
      pollInProgressRef.current = false
      cancelledRef.current = true
      setBackfilling(false)
    }
  }, [foodAreaId])

  useEffect(() => {
    const justFinishedLoading = prevLoadingRef.current && !isLoading

    if (
      !foodAreaId ||
      isLoading ||
      restaurantCount > 0 ||
      pollInProgressRef.current ||
      polledAreaRef.current === foodAreaId ||
      // Only kick in once the initial fetch has actually settled empty; never
      // pre-empt the in-flight initial fetch (keeps the populated case clean).
      !justFinishedLoading
    ) {
      return
    }

    polledAreaRef.current = foodAreaId
    pollInProgressRef.current = true
    cancelledRef.current = false
    setBackfilling(true)

    let attempt = 0
    let timeoutId: ReturnType<typeof setTimeout>

    const finish = (): void => {
      pollInProgressRef.current = false
      setBackfilling(false)
    }

    const runPoll = async (): Promise<void> => {
      if (cancelledRef.current) return
      try {
        const result = await dispatch(
          getFoodAreaRestaurants({ foodAreaId, latitude, longitude }),
        )
        if (cancelledRef.current) return
        const populated =
          getFoodAreaRestaurants.fulfilled.match(result) &&
          Array.isArray(result.payload) &&
          result.payload.length > 0
        if (populated) {
          finish()
          return
        }
      } catch {
        // ignore; will stop at max attempts
      }
      attempt++
      if (attempt < DEFAULT_MAX_ATTEMPTS) scheduleNext()
      else finish()
    }

    const scheduleNext = (): void => {
      if (cancelledRef.current) return
      const delay = DEFAULT_POLL_AFTER_MS + attempt * BACKOFF_MS
      timeoutId = setTimeout(() => {
        void runPoll()
      }, delay)
    }

    scheduleNext()

    return () => {
      cancelledRef.current = true
      clearTimeout(timeoutId)
    }
  }, [foodAreaId, latitude, longitude, restaurantCount, isLoading, dispatch])

  // Track previous loading value (after the poll-start effect has read it).
  useEffect(() => {
    prevLoadingRef.current = isLoading
  }, [isLoading])

  return backfilling
}
